import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { validateTemplate } from "../../../api/postCall/validateTemplate";
import {
  TemplateValidatorResponse,
  TemplateValidatorResponseItem,
  ValidateTemplateRequestItem,
} from "../../../data-model/templateValidatorModel";
import { useAbortableFetch } from "../../../hooks/useAbortableFetch";
import { shouldProceedWithoutReview } from "../../../utils/templateValidatorUtils";
import { ToastMessages } from "../../toasts/toastMessages";

interface UseCommentsWritebackValidationParams {
  validationPayload: ValidateTemplateRequestItem[];
  onProceed: () => void;
}

const validationFetchFn = async (
  signal: AbortSignal,
  payload: ValidateTemplateRequestItem[],
  proceed: () => void,
): Promise<TemplateValidatorResponse> => {
  const filtered =
    Array.isArray(payload) && payload.length > 0
      ? payload.filter((p) => (p.template_text?.trim() ?? "") !== "")
      : [];

  if (filtered.length === 0) {
    proceed();
    return { all_success: true, results: [] };
  }

  const response = await validateTemplate(filtered, signal);
  if (shouldProceedWithoutReview(response.results)) {
    proceed();
  }
  return response;
};

export function useCommentsWritebackValidation({
  validationPayload,
  onProceed,
}: UseCommentsWritebackValidationParams) {
  const [isValidating, setIsValidating] = useState(true);
  const [
    lastSuccessfulValidationPayloadKey,
    setLastSuccessfulValidationPayloadKey,
  ] = useState<string | null>(null);
  const { runFetch, abort, data, loading } =
    useAbortableFetch<TemplateValidatorResponse>(validationFetchFn);

  const onProceedRef = useRef(onProceed);
  onProceedRef.current = onProceed;
  const validationPayloadKey = JSON.stringify(validationPayload);

  useEffect(() => {
    setIsValidating(true);
    setLastSuccessfulValidationPayloadKey(null);
    const proceed = () => onProceedRef.current();
    runFetch(validationPayload, proceed).then(
      (result) => {
        setIsValidating(false);
        if (result.success) {
          setLastSuccessfulValidationPayloadKey(validationPayloadKey);
        }
        if (!result.success && result.error?.name !== "AbortError") {
          toast.error(ToastMessages.WRITEBACK_VALIDATION_ERROR);
          proceed();
        }
      },
      () => setIsValidating(false),
    );
    return () => {
      abort();
      setIsValidating(false);
    };
  }, [validationPayloadKey]);

  const results: TemplateValidatorResponseItem[] = data?.results ?? [];

  return {
    loading: isValidating || loading,
    results,
    abort,
    validationPayloadKey,
    lastSuccessfulValidationPayloadKey,
    validationResponse: data,
  };
}
